import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CalendarPlus } from 'lucide-react';

import usePage from '@dashboard/hooks/usePage';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useUrlState from '@dashboard/hooks/useUrlState';
import useTableSort from '@dashboard/hooks/useTableSort';
import {
  BOOKING_FILTER_OPTIONS,
  BOOKINGS_LIST_ORDER_STATUS,
} from '@dashboard/lib/chargesConstants';
import { getStatusFromQuery } from '@dashboard/lib/tableHelpers';

import { PaymentsBookingsList } from '@dashboard/scenes/PaymentsBookings';

import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import ExportView from '@dashboard/components/ExportView';
import FilterInput from '@dashboard/components/Form/components/FilterInput';
import SearchInput from '@dashboard/components/Form/components/SearchInput';
import { Button } from '@dashboard/components/ui/button';
import Tooltip from '@dashboard/components/Tooltip';
import { getAddBookingDisabledReason } from './AddBooking/lib/addBookingDisabledReason';

export const GUEST_FILTER_OPTIONS = {
  ALL: { value: '', label: 'All attendee details' },
  missing: { value: 'missing', label: 'Missing attendee details' },
  flagged: { value: 'flagged', label: 'Flagged for review' },
  complete: { value: 'complete', label: 'Details complete' },
};

const PageBookings = () => {
  const { page } = usePage();
  const { isConnectedToStripe } = useAuth();
  const location = useLocation();
  const addBookingDisabledReason = getAddBookingDisabledReason({ isConnectedToStripe, page });
  const [query, setParams] = useUrlState();
  const { sorting, onSortingChange, sort } = useTableSort();
  const ticketGroups = useMemo(() => page.ticketGroups || [], [page.ticketGroups]);

  const search = query.search || '';
  const status = getStatusFromQuery(query.status, BOOKING_FILTER_OPTIONS);
  const collectsGuests = Boolean(page.eventDetails?.attendeeDetails?.enabled);
  // Links saved before the rename still say ?guests=.
  const attendeesParam = query.attendees ?? query.guests;
  const guestFilter =
    collectsGuests && GUEST_FILTER_OPTIONS[attendeesParam] ? attendeesParam : 'ALL';
  const ticketGroupFilter = query.ticketGroup || 'ALL';

  const ticketGroupOptions = useMemo(
    () => ({
      ALL: {
        value: '',
        label: 'All ticket groups',
      },
      ...((ticketGroups || []).reduce((acc, group) => {
        acc[group._id] = {
          value: group._id,
          label: group.name,
        };
        return acc;
      }, {}) || {}),
    }),
    [ticketGroups],
  );

  const selectedTicketGroupId = ticketGroupOptions[ticketGroupFilter]?.value;

  const ticketTypeOptions = useMemo(
    () => ({
      ALL: {
        value: '',
        label: 'All tickets',
      },
      ...((ticketGroups || []).reduce((acc, group) => {
        if (ticketGroupFilter !== 'ALL' && group._id !== selectedTicketGroupId) {
          return acc;
        }

        group.ticketTypes?.forEach(ticketType => {
          acc[ticketType._id] = {
            value: ticketType._id,
            label: `${ticketType.name} (${group.name})`,
          };
        });
        return acc;
      }, {}) || {}),
    }),
    [selectedTicketGroupId, ticketGroupFilter, ticketGroups],
  );

  // If the URL has a ticketType that's not valid for the current group, treat as ALL
  const effectiveTicketType = ticketTypeOptions[query.ticketType] ? query.ticketType : 'ALL';

  const ticketFilters = {
    ...(ticketGroupOptions[ticketGroupFilter]?.value && {
      ['tickets.ticketGroup']: ticketGroupOptions[ticketGroupFilter].value,
    }),
    ...(ticketTypeOptions[effectiveTicketType]?.value && {
      ['tickets.ticketType']: ticketTypeOptions[effectiveTicketType].value,
    }),
  };

  const filter = {
    ...(BOOKING_FILTER_OPTIONS[status].value !== 'all' && {
      status: BOOKING_FILTER_OPTIONS[status].value,
    }),
    ...ticketFilters,
    orderStatus: BOOKINGS_LIST_ORDER_STATUS,
    ...(GUEST_FILTER_OPTIONS[guestFilter].value && {
      attendeeStatus: GUEST_FILTER_OPTIONS[guestFilter].value,
    }),
    page: page._id,
    livemode: true,
    search,
  };

  const handleSearchChange = value => setParams({ search: value || undefined });

  const handleStatusChange = key =>
    setParams({
      status:
        BOOKING_FILTER_OPTIONS[key].value === 'all' ? undefined : BOOKING_FILTER_OPTIONS[key].value,
    });

  const handleTicketGroupChange = key =>
    setParams({
      ticketGroup: key === 'ALL' ? undefined : key,
      ticketType: undefined, // reset ticket type when group changes
    });

  const handleTicketTypeChange = key => setParams({ ticketType: key === 'ALL' ? undefined : key });

  const handleGuestFilterChange = key =>
    setParams({ attendees: key === 'ALL' ? undefined : key, guests: undefined });

  return (
    <div className="mb-24">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 pb-4">
        <CardHeaderTitle variant="page">Bookings</CardHeaderTitle>

        <div className="flex flex-wrap items-center gap-2 max-lg:w-full [&>*:first-child]:min-w-0 max-lg:[&>*:first-child]:flex-1 max-lg:[&>*:first-child]:basis-56 max-sm:[&>*:first-child]:basis-full">
          <SearchInput
            onChange={handleSearchChange}
            name="search_customers"
            placeholder="Search by name, email or ID"
            initialValue={search}
          />

          <FilterInput
            options={ticketGroupOptions}
            onChange={handleTicketGroupChange}
            value={ticketGroupFilter}
          />

          <FilterInput
            options={ticketTypeOptions}
            onChange={handleTicketTypeChange}
            value={effectiveTicketType}
          />

          {collectsGuests ? (
            <FilterInput
              options={GUEST_FILTER_OPTIONS}
              onChange={handleGuestFilterChange}
              value={guestFilter}
            />
          ) : null}

          <FilterInput
            options={BOOKING_FILTER_OPTIONS}
            onChange={handleStatusChange}
            value={status}
          />

          <ExportView
            collection="payments"
            label="payments"
            filters={{
              ...filter,
              page: page._id,
              sort: JSON.stringify(sort),
            }}
          />

          {addBookingDisabledReason ? (
            <Tooltip label={addBookingDisabledReason} asChild>
              {/* A disabled button fires no pointer events, so the tooltip needs a wrapper. */}
              <span tabIndex={0} className="inline-flex">
                <Button disabled>
                  <CalendarPlus className="size-3.5 mr-1.5" />
                  Add booking
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button asChild>
              <Link
                to={{
                  pathname: `/pages/${page._id}/add-booking`,
                  state: { from: `${location.pathname}${location.search}` },
                }}
              >
                <CalendarPlus className="size-3.5 mr-1.5" />
                Add booking
              </Link>
            </Button>
          )}
        </div>
      </div>

      <PaymentsBookingsList
        sort={sort}
        search={search}
        filter={filter}
        options={{
          hidePaymentLink: true,
          hidePageType: true,
          showGuestDetails: collectsGuests,
        }}
        sorting={sorting}
        onSortingChange={onSortingChange}
      />
    </div>
  );
};

PageBookings.propTypes = {
  page: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
};

export default PageBookings;
